'use client';
import * as React from 'react';
import { ChevronDown } from 'lucide-react';
import { format } from 'date-fns';
import { clsx } from 'clsx';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useIsMobile } from '@/hooks/use-mobile';

interface DatePickerProps {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  fromYear?: number;
  toYear?: number;
  minDate?: Date;
  maxDate?: Date;
}

export function DatePicker({
  value,
  onChange,
  placeholder = 'Select date',
  disabled = false,
  className,
  fromYear = 1800,
  toYear = 2100,
  minDate,
  maxDate,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [month, setMonth] = React.useState<Date | undefined>(
    value || new Date(),
  );
  // Desktop keeps the fixed "always below the trigger" placement. On phones
  // that puts the calendar off screen whenever the trigger sits in the lower
  // part of the viewport, so there the popover is allowed to flip and shift
  // into view and to scroll within the space that is left.
  const isMobile = useIsMobile();

  React.useEffect(() => {
    if (open) {
      setMonth(value || new Date());
    }
  }, [open]);

  const handleSelectToday = () => {
    const today = new Date();
    onChange?.(today);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            'tw-w-full tw-justify-between tw-font-normal',
            !value && 'tw-text-muted-foreground',
            className,
          )}
          disabled={disabled}
        >
          {value ? format(value, 'MM/dd/yyyy') : placeholder}
          <ChevronDown className="tw-h-4 tw-w-4 tw-opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={clsx(
          '!tw-p-0 tw-min-w-[250px] tw-w-auto tw-max-w-[320px]',
          'tw-animate-in tw-fade-in tw-zoom-in-95 tw-duration-200',
          isMobile &&
            'tw-max-h-[var(--radix-popover-content-available-height)] tw-overflow-y-auto',
        )}
        align="start"
        side="bottom"
        sideOffset={4}
        avoidCollisions={isMobile}
        collisionPadding={isMobile ? 8 : 0}
        sticky={isMobile ? 'partial' : 'always'}
      >
        <Calendar
          mode="single"
          selected={value}
          month={month}
          onMonthChange={setMonth}
          captionLayout="dropdown"
          fromYear={fromYear}
          toYear={toYear}
          minDate={minDate}
          maxDate={maxDate}
          onSelect={(date) => {
            onChange?.(date);
            setOpen(false);
          }}
          disabled={disabled}
        />
        <div className="tw-border-t tw-p-3">
          <Button
            variant="outline"
            className="tw-w-full"
            onClick={handleSelectToday}
            disabled={disabled}
          >
            Today
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
